import { Field } from '../apiInterfaces';
import { SSEL_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import { getDiscoverFilterFieldsAPI, getDiscoverFilterValuesAPI } from './api';
import {
  DiscoverAllTopicsRequest,
  DiscoverDashboardArticleCountResponse,
  DiscoverExcludedMetricResponse,
  DiscoverGetFilterFieldsResponse,
  DiscoverGetFiltersRequest,
  DiscoverGetFiltersResponse,
  DiscoverTopicAllTicketsRequest,
  DiscoverTopicAllTicketsResponse,
  DiscoverTopicPolicyRelevantTicketsResponse,
  DiscoverTopicVoiceCallsRequest,
  DiscoverTopicVoiceCallsResponse,
  DiscoverVerifyArticleRequest,
  DiscoverVerifyArticleResponse,
} from './types';
import omit from 'lodash/fp/omit';
import { createOption } from 'src/components/analytic-filter/helper';
import { FilterOption } from 'src/components/analytic-filter/types';
import { ColumnItem } from 'src/components/dashboard-pages/discover-all-topics-page/AllTopicsTableV3/constants';
import { dateRangeToTimestamp } from 'src/components/dashboard-pages/solve-insights/helpers';
import { patchArticleInStoreAction } from 'src/reducers/discoverReducer/discoverReducer';
import {
  ArticleContentLabel,
  ArticleFilterOptions,
  AutomateTopicRequest,
  BulkPatchArticlesRequest,
  CreateContentLabelsRequest,
  DiscoverAllTopicsDTO,
  DiscoverAllTopicsResponse,
  DiscoverArticle,
  DiscoverMetadata,
  DiscoverTicket,
  DiscoverTopic,
  EditContentLabelRequest,
  KnowledgeGapArticleRecommendationsV2,
  KnowledgeGapArticleRecommendationsV2Request,
  PatchArticleRequest,
} from 'src/reducers/discoverReducer/types';
import { patchGeneratedArticle } from 'src/slices/solve-insights/solveInsightsSlice';
import {
  DiscoverAutomationsByTypeResponse,
  DiscoverRecommendationsPoliciesResponse,
  ExcludedTicketRequest,
  TicketCounterRequest,
  TicketCounterResponse,
} from 'src/types/DiscoverTypes';
import { constructQueryParamWithTimeFilter } from 'src/utils/discover/helpers';

const discoverRoute = '/ssel/discover/v1';
const discoverV0Route = '/ssel/discover';

const discoverApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    automateTopic: builder.mutation<DiscoverTopic, AutomateTopicRequest>({
      invalidatesTags: ['Automations'],
      query: body => ({
        body,
        method: 'POST',
        url: `${discoverV0Route}/topics/automate`,
      }),
    }),
    createContentLabels: builder.mutation<
      ArticleContentLabel[],
      CreateContentLabelsRequest
    >({
      invalidatesTags: ['ArticleContentLabels'],
      query: body => ({
        body,
        method: 'POST',
        url: `${discoverRoute}/content-labels`,
      }),
    }),
    deleteContentLabel: builder.mutation<void, { label_id: string }>({
      invalidatesTags: ['ArticleContentLabels', 'Automations'],
      query: ({ label_id }) => ({
        method: 'DELETE',
        url: `${discoverRoute}/content-labels/${label_id}`,
      }),
    }),
    editContentLabel: builder.mutation<
      ArticleContentLabel,
      { body: EditContentLabelRequest; label_id: string }
    >({
      invalidatesTags: ['ArticleContentLabels'],
      query: ({ body, label_id }) => ({
        body,
        method: 'PUT',
        url: `${discoverRoute}/content-labels/${label_id}`,
      }),
    }),
    excludedTicketMetrics: builder.query<
      DiscoverExcludedMetricResponse,
      ExcludedTicketRequest
    >({
      extraOptions: { maxRetries: 0 },
      query: params => ({
        body: params,
        method: 'POST',
        url: `${SSEL_BASE_URL}/discover/v1/tickets-v2`,
      }),
    }),
    getAllFilterFields: builder.query<DiscoverGetFilterFieldsResponse, void>({
      query: () => ({
        url: '/analytics/filter-fields?curate_fields=True',
      }),
    }),
    getAllFilters: builder.query<
      DiscoverGetFiltersResponse,
      DiscoverGetFiltersRequest
    >({
      query: ({ filters }) => {
        return {
          body: filters,
          method: 'POST',
          url: '/analytics/v2/filter-values',
        };
      },
    }),
    getAllTopics: builder.query<DiscoverAllTopicsDTO, DiscoverAllTopicsRequest>(
      {
        extraOptions: { maxRetries: 0 },
        providesTags: ['Automations'],
        query: ({ dataFilterQuery, keyword_search, timeFilter }) => {
          const params = constructQueryParamWithTimeFilter(
            timeFilter,
            keyword_search,
          );
          const url = `${discoverRoute}/topics?${params}`;
          return {
            body: dataFilterQuery,
            method: 'POST',
            url,
          };
        },
        transformResponse: (
          response: DiscoverAllTopicsResponse,
        ): DiscoverAllTopicsDTO => {
          const updatedDynamicColumns: ColumnItem[] =
            response.dynamic_columns_for_customized_metrics?.map(it => ({
              accessorKey: it.accessor_key,
              header: it.display_name,
              isColorInverted: !it.is_color_inverted,
              isPercentChange: it.is_percent_change,
              metricsType: it.metrics_type,
              relatedKey: it.related_key,
              size: 240,
            })) ?? [];
          return {
            ...response,
            dynamic_columns_for_customized_metrics: updatedDynamicColumns,
          };
        },
      },
    ),
    getAllTopicTickets: builder.query<
      DiscoverTopicAllTicketsResponse,
      DiscoverTopicAllTicketsRequest
    >({
      query: ({ dataFilterQuery, searchText, timeFilter, topicId }) => {
        const params = constructQueryParamWithTimeFilter(
          timeFilter,
          searchText,
        );
        const url = `${discoverRoute}/topics/${topicId}/tickets?${params}`;
        return {
          body: dataFilterQuery.length
            ? { filters: dataFilterQuery }
            : undefined,
          method: 'POST',
          url,
        };
      },
    }),
    getArticleCounts: builder.query<
      DiscoverDashboardArticleCountResponse,
      void
    >({
      providesTags: ['ArticleContentLabels'],
      query: () => ({
        method: 'GET',
        url: `${discoverRoute}/articles/counts`,
      }),
    }),
    getArticleFilterOptions: builder.query<ArticleFilterOptions, void>({
      providesTags: ['Automations'],
      query: () => ({
        method: 'GET',
        url: `${SSEL_BASE_URL}/discover/v1/articles/filter-options`,
      }),
    }),
    getArticleTickets: builder.query<{ tickets: DiscoverTicket[] }, string>({
      query: id => ({
        method: 'GET',
        url: `${SSEL_BASE_URL}/discover/v1/article/${id}/tickets`,
      }),
    }),
    getAutomations: builder.query<
      DiscoverAutomationsByTypeResponse[],
      { includeAutomationValue: boolean }
    >({
      providesTags: ['Automations'],
      query: ({ includeAutomationValue }) => ({
        method: 'GET',
        params: { include_automation_value: includeAutomationValue },
        url: SSEL_BASE_URL + '/discover/automations',
      }),
    }),
    getContentLabels: builder.query<ArticleContentLabel[], void>({
      providesTags: ['ArticleContentLabels'],
      query: () => ({
        method: 'GET',
        url: `${discoverRoute}/content-labels`,
      }),
    }),
    getFilterOptions: builder.query<FilterOption[], void>({
      queryFn: async () => {
        const filterFields = await getDiscoverFilterFieldsAPI();

        // flatten object to a single list and remove non STRING field_types
        const flattenedFilterFields = Object.entries(filterFields)
          .flatMap(([key, value]: [string, (Field & { category: string })[]]) =>
            value.map(value => ({ ...value, category: key })),
          )
          .filter(field => field.field_type === 'STRING');

        const results = await Promise.all(
          flattenedFilterFields.map(async field => {
            const fieldValueChoices = await getDiscoverFilterValuesAPI(
              field.category,
              field.field_id,
            );
            const values = fieldValueChoices?.values ?? [];
            if (values.length) {
              return createOption(field.category, field, values);
            } else {
              return null;
            }
          }),
        );

        return {
          data: results.filter(
            (result): result is FilterOption => result !== null,
          ),
        };
      },
    }),
    getKnowledgeGapRecommendationsPagination: builder.query<
      KnowledgeGapArticleRecommendationsV2,
      KnowledgeGapArticleRecommendationsV2Request
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.articles = [...newItems.articles];
        } else {
          currentCache.articles = [
            ...currentCache.articles,
            ...newItems.articles,
          ];
        }
        currentCache.banner_recommendation_value =
          newItems.banner_recommendation_value;
      },
      providesTags: ['Automations'],
      query: params => ({
        body: params,
        method: 'POST',
        url: `${SSEL_BASE_URL}/discover/v1/articles/recommendations-v2`,
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return omit(['page'], queryArgs);
      },
    }),
    getMetadata: builder.query<DiscoverMetadata, void>({
      query: () => ({
        method: 'GET',
        url: `${SSEL_BASE_URL}/discover/metadata`,
      }),
    }),
    getRecommendedPolicies: builder.query<
      DiscoverRecommendationsPoliciesResponse,
      void
    >({
      providesTags: ['Automations'],
      query: () => ({
        method: 'POST',
        url: `${SSEL_BASE_URL}/discover/v1/policies/recommendations`,
      }),
    }),
    getTicketCount: builder.query<TicketCounterResponse, TicketCounterRequest>({
      extraOptions: { maxRetries: 0 },
      query: ({ dataFilterQuery, dateRange }) => {
        const { end_timestamp, start_timestamp } =
          dateRangeToTimestamp(dateRange);
        const body = {
          end_timestamp,
          filters: dataFilterQuery.length
            ? { filters: dataFilterQuery }
            : undefined,
          start_timestamp,
        };
        return {
          body,
          method: 'POST',
          url: `${SSEL_BASE_URL}/discover/v1/tickets`,
        };
      },
    }),
    getTopicPolicyRelevantTickets: builder.query<
      DiscoverTopicPolicyRelevantTicketsResponse,
      { policyId: string; topicId: string }
    >({
      providesTags: ['PolicyTickets'],
      query: ({ policyId, topicId }) => ({
        method: 'GET',
        url: `${discoverRoute}/topics/${topicId}/policies/${policyId}/policy-tickets`,
      }),
    }),
    getTopicVoiceCalls: builder.query<
      DiscoverTopicVoiceCallsResponse,
      DiscoverTopicVoiceCallsRequest
    >({
      query: ({ end_timestamp, limit, start_timestamp, topicId }) => ({
        method: 'GET',
        params: {
          end_timestamp,
          limit,
          start_timestamp,
        },
        url: `${discoverRoute}/topics/${topicId}/voice-calls`,
      }),
    }),
    getVoiceTopicsV2: builder.query<
      DiscoverAllTopicsDTO,
      DiscoverAllTopicsRequest
    >({
      extraOptions: { maxRetries: 0 },
      providesTags: ['Automations'],
      query: ({ dataFilterQuery, keyword_search, timeFilter }) => {
        const params = constructQueryParamWithTimeFilter(
          timeFilter,
          keyword_search,
        );
        const url = `${discoverRoute}/voice-topics-v2?${params}`;
        return {
          body: dataFilterQuery,
          method: 'POST',
          url,
        };
      },
      transformResponse: (
        response: DiscoverAllTopicsResponse,
      ): DiscoverAllTopicsDTO => {
        const updatedDynamicColumns: ColumnItem[] =
          response.dynamic_columns_for_customized_metrics?.map(it => ({
            accessorKey: it.accessor_key,
            header: it.display_name,
            isColorInverted: !it.is_color_inverted,
            isPercentChange: it.is_percent_change,
            metricsType: it.metrics_type,
            relatedKey: it.related_key,
            size: 240,
          })) ?? [];
        return {
          ...response,
          dynamic_columns_for_customized_metrics: updatedDynamicColumns,
        };
      },
    }),
    patchArticle: builder.mutation<
      DiscoverArticle,
      { articleId: string; patchData: PatchArticleRequest }
    >({
      invalidatesTags: (result, error, { patchData }) => {
        const tags: Array<'Automations' | 'ArticleContentLabels'> = [
          'Automations',
        ];
        if (patchData.labels_to_add || patchData.labels_to_remove) {
          tags.push('ArticleContentLabels');
        }
        return tags;
      },
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        // Note: knowledgeGapRecommendations handled by invalidation by tag 'Automations'
        dispatch(patchArticleInStoreAction(data));
        // Also update solve insights generated articles
        dispatch(patchGeneratedArticle(data));
      },
      query: ({ articleId, patchData }) => ({
        body: patchData,
        method: 'PATCH',
        url: `${SSEL_BASE_URL}/discover/v1/article/${articleId}`,
      }),
    }),
    patchArticlesBulk: builder.mutation<
      DiscoverArticle[],
      BulkPatchArticlesRequest
    >({
      invalidatesTags: (result, error, { fields_to_update }) => {
        const tags: Array<'Automations' | 'ArticleContentLabels'> = [
          'Automations',
        ];
        if (
          fields_to_update.labels_to_add ||
          fields_to_update.labels_to_remove
        ) {
          tags.push('ArticleContentLabels');
        }
        return tags;
      },
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        // Dispatch patch action for each article in the response
        data.forEach(article => {
          // Note: knowledgeGapRecommendations handled by invalidation by tag 'Automations'
          dispatch(patchArticleInStoreAction(article));
          // Also update solve insights generated articles
          dispatch(patchGeneratedArticle(article));
        });
      },
      query: body => ({
        body,
        method: 'PATCH',
        url: `${SSEL_BASE_URL}/discover/v1/articles/bulk_patch`,
      }),
    }),
    updateArticle: builder.mutation<void, DiscoverVerifyArticleRequest>({
      query: ({ article_id }) => ({
        method: 'PUT',
        url: `${SSEL_BASE_URL}/discover/v1/article/${article_id}/update`,
      }),
    }),
    verifyArticle: builder.query<
      DiscoverVerifyArticleResponse,
      DiscoverVerifyArticleRequest
    >({
      query: ({ article_id }) => ({
        method: 'GET',
        url: `${SSEL_BASE_URL}/discover/v1/article/${article_id}/can-update`,
      }),
      transformResponse: (response: DiscoverVerifyArticleResponse) => {
        if (!response || !Array.isArray(response)) {
          return [false, null];
        }
        return response;
      },
    }),
  }),
});

export const {
  useAutomateTopicMutation,
  useCreateContentLabelsMutation,
  useDeleteContentLabelMutation,
  useEditContentLabelMutation,
  useExcludedTicketMetricsQuery,
  useGetAllFilterFieldsQuery,
  useGetAllFiltersQuery,
  useGetAllTopicsQuery,
  useGetAllTopicTicketsQuery,
  useGetArticleCountsQuery,
  useGetArticleFilterOptionsQuery,
  useGetArticleTicketsQuery,
  useGetAutomationsQuery,
  useGetContentLabelsQuery,
  useGetFilterOptionsQuery,
  useGetKnowledgeGapRecommendationsPaginationQuery,
  useGetMetadataQuery,
  useGetRecommendedPoliciesQuery,
  useGetTicketCountQuery,
  useGetTopicPolicyRelevantTicketsQuery,
  useGetTopicVoiceCallsQuery,
  useGetVoiceTopicsV2Query,
  usePatchArticleMutation,
  usePatchArticlesBulkMutation,
  useUpdateArticleMutation,
  useVerifyArticleQuery,
} = discoverApi;

export { discoverApi };
